import { IAdminRouteGroup } from "../@types/AdminRoute";
import { Alert } from "../assets/icons/Alert";
import { Book } from "../assets/icons/Book";
import { Building } from "../assets/icons/Building";
import { Calendar } from "../assets/icons/Calendar";
import { Database } from "../assets/icons/Database";
import { DollarBill } from "../assets/icons/DollarBill";
import { Email } from "../assets/icons/Email";
import { File } from "../assets/icons/File";
import { Forms } from "../assets/icons/Forms";
import { Globe } from "../assets/icons/Globe";
import { Home } from "../assets/icons/Home";
import { Hospital } from "../assets/icons/Hospital";
import { Lab } from "../assets/icons/Lab";
import { Link } from "../assets/icons/Link";
import { Message } from "../assets/icons/Message";
import { Note } from "../assets/icons/Note";
import { Panel } from "../assets/icons/Panel";
import { People } from "../assets/icons/People";
import { PhoneDevice } from "../assets/icons/PhoneDevice";
import { PhoneDeviceAdmin } from "../assets/icons/PhoneDeviceAdmin";
import { Pill } from "../assets/icons/Pill";
import { PinnedGraph } from "../assets/icons/PinnedGraph";
import { Prom } from "../assets/icons/Prom";
import { ProtocolActions } from "../assets/icons/ProtocolActions";
import { ResponseIcon } from "../assets/icons/ResponseIcon";
import { RuleIcon } from "../assets/icons/RuleIcon";
import { Settings } from "../assets/icons/Settings";
import { Spirometry } from "../assets/icons/Spirometry";
import { TableSettings } from "../assets/icons/TableSettings";
import { Vitals } from "../assets/icons/Vitals";
import { isDevOrStaging } from "../utils/environment";
import { hasPermission, PERMISSIONS } from "../utils/hasPermission";

/**
 * Side-menu structure for the /admin section (EN-5129) — the in-portal
 * replacement for the Retool internal tools, restricted to SuperAdmin
 * users with a @patientmpower.com email (see AdminLayout).
 *
 * Only REAL, API-backed pages are listed (the PoC mock pages were removed
 * on 2026-08-24 so the menu reflects actual migration progress). Each
 * epic ticket adds its page here with its `requiredPermission` — see the
 * per-page recipe in retool-migration.md.
 */
export const adminMenuGroups: IAdminRouteGroup[] = [
  {
    name: "Home",
    Icon: Home,
    path: "/admin",
  },
  {
    name: "Devices",
    Icon: PhoneDeviceAdmin,
    children: [
      {
        name: "Billable devices",
        path: "/admin/devices/billable",
        Icon: DollarBill,
        requiredPermission: PERMISSIONS.BILLABLE_DEVICES_ACCESS,
      },
      {
        name: "Cellular devices",
        path: "/admin/devices/cellular",
        Icon: PhoneDevice,
        requiredPermission: PERMISSIONS.CELLULAR_DEVICES_ACCESS,
      },
      {
        name: "Firmware",
        path: "/admin/devices/firmware",
        Icon: PhoneDeviceAdmin,
        requiredPermission: PERMISSIONS.FIRMWARE_ACCESS,
      },
    ],
  },
  {
    name: "Surveys",
    Icon: Prom,
    children: [
      {
        name: "Surveys",
        path: "/admin/surveys",
        Icon: Prom,
        requiredPermission: PERMISSIONS.SURVEYS_ACCESS,
      },
      {
        // EN-5261: temporary — copies this region's surveys into Global.
        name: "Survey sync",
        path: "/admin/surveys/sync",
        Icon: Prom,
        requiredPermission: PERMISSIONS.SURVEYS_ACCESS,
      },
    ],
  },
  {
    // Retool grouped these under "Tenants / Hospitals / Wards"; Hospitals
    // (EN-5152), Tenants (EN-5153) and Ward Measurement Types (EN-5157) join
    // this group as they migrate.
    name: "Organisation",
    Icon: Building,
    children: [
      {
        name: "Tenants",
        path: "/admin/tenants",
        Icon: Globe,
        requiredPermission: PERMISSIONS.TENANTS_ACCESS,
      },
      {
        name: "Hospitals",
        path: "/admin/hospitals",
        Icon: Hospital,
        requiredPermission: PERMISSIONS.HOSPITALS_ACCESS,
      },
      {
        name: "Wards",
        path: "/admin/wards",
        Icon: Building,
        requiredPermission: PERMISSIONS.WARDS_ACCESS,
      },
      {
        name: "Ward measurement types",
        path: "/admin/wards/measurement-types",
        Icon: TableSettings,
        requiredPermission: PERMISSIONS.WARD_MEASUREMENT_TYPES_ACCESS,
      },
      {
        name: "Studies",
        path: "/admin/studies",
        Icon: Calendar,
        requiredPermission: PERMISSIONS.STUDIES_ACCESS,
      },
    ],
  },
  {
    name: "Staff",
    Icon: People,
    path: "/admin/staff",
    requiredPermission: PERMISSIONS.STAFF_ACCESS,
  },
  {
    // Retool's "Patients" group.
    name: "Patients",
    Icon: Panel,
    children: [
      {
        // "All patients", not "Patients": the group is already called that,
        // and the entry is the unfiltered list the other entries narrow.
        name: "All patients",
        path: "/admin/patients",
        Icon: Panel,
        requiredPermission: PERMISSIONS.PATIENTS_ACCESS,
      },
      {
        // Shares patients:access rather than carrying a code of its own
        // (EN-5171): the list above does not filter archived, so that grant
        // already reaches every record this page shows.
        name: "Archived patients",
        path: "/admin/patients/archived",
        Icon: File,
        requiredPermission: PERMISSIONS.PATIENTS_ACCESS,
      },
      {
        // EN-5222: bulk-migrate a hospital's legacy-app patients to Cognito,
        // issuing a random password each. Its own permission: the page shows
        // credentials, so it is granted to fewer people than the list above.
        name: "Cognito migration",
        path: "/admin/patients/cognito-migration",
        Icon: PhoneDeviceAdmin,
        requiredPermission: PERMISSIONS.COGNITO_MIGRATION_ACCESS,
      },
      {
        name: "Measurements",
        path: "/admin/patients/measurements",
        Icon: Vitals,
        requiredPermission: PERMISSIONS.MEASUREMENTS_ACCESS,
      },
      {
        name: "Treatments",
        path: "/admin/patients/treatments",
        Icon: Pill,
        requiredPermission: PERMISSIONS.TREATMENTS_ACCESS,
      },
      {
        name: "1MSTS tests",
        path: "/admin/patients/1msts",
        Icon: Spirometry,
        requiredPermission: PERMISSIONS.ONE_MSTS_ACCESS,
      },
      {
        name: "Spirometry sessions",
        path: "/admin/patients/spirometry",
        Icon: Spirometry,
        requiredPermission: PERMISSIONS.SPIROMETRY_ACCESS,
      },
      {
        name: "Baselines",
        path: "/admin/patients/baselines",
        Icon: PinnedGraph,
        requiredPermission: PERMISSIONS.BASELINES_ACCESS,
      },
      {
        // Alert *configurations* (EN-5165). Triggered alerts live on the
        // patient's own record, not here.
        name: "Alerts",
        path: "/admin/alerts",
        Icon: Alert,
        requiredPermission: PERMISSIONS.ALERTS_ACCESS,
      },
      {
        name: "Workflows",
        path: "/admin/workflows",
        Icon: ProtocolActions,
        requiredPermission: PERMISSIONS.WORKFLOWS_ACCESS,
      },
    ],
  },
  {
    name: "Integrations",
    Icon: Link,
    children: [
      {
        name: "eMED Beaumont integration",
        path: "/admin/integrations/emed-beaumont",
        Icon: Lab,
        requiredPermission: PERMISSIONS.EMED_BEAUMONT_ACCESS,
      },
      {
        // Dev + staging only. The Retool page it replaces read a hard-wired
        // staging database, so it never had a production meaning. EN-5174.
        name: "Integration queue",
        path: "/admin/integrations/queue",
        Icon: Message,
        requiredPermission: PERMISSIONS.EMED_BEAUMONT_ACCESS,
        isAvailable: isDevOrStaging,
      },
      {
        name: "Jupyter integration",
        path: "/admin/integrations/jupyter",
        Icon: Note,
        requiredPermission: PERMISSIONS.JUPYTER_INTEGRATION_ACCESS,
      },
      {
        // EN-5203: Transmural Platform orders and prescriber-to-ward mappings.
        name: "Transmural Platform",
        path: "/admin/integrations/tmp",
        Icon: Hospital,
        requiredPermission: PERMISSIONS.TMP_INTEGRATION_ACCESS,
      },
      {
        // EN-5232: connected Dexcom patients and their sync history.
        name: "Dexcom integration",
        path: "/admin/integrations/dexcom",
        Icon: PhoneDevice,
        requiredPermission: PERMISSIONS.DEXCOM_INTEGRATION_ACCESS,
      },
      {
        // Machine credentials for the integration API, Retool and webhooks.
        name: "API keys",
        path: "/admin/integrations/api-keys",
        Icon: Settings,
        requiredPermission: PERMISSIONS.INTEGRATION_API_KEYS_ACCESS,
      },
    ],
  },
  {
    // Retool's "Translations" group. The master translations editor and
    // its publish flow are the epic's last ticket (EN-5178) and join here;
    // survey translations are plain pMp API and migrate early.
    name: "Translations",
    Icon: Book,
    children: [
      {
        name: "Survey translations",
        path: "/admin/translations/surveys",
        Icon: Book,
        requiredPermission: PERMISSIONS.SURVEY_TRANSLATIONS_ACCESS,
      },
      {
        // EN-5178: CRUD on the master translations store (ex Retool-hosted
        // DB, now on the staging Postgres server), shared by every
        // environment — so, like the sync page, it exists everywhere.
        name: "Master translations",
        path: "/admin/translations/keys",
        Icon: Book,
        requiredPermission: PERMISSIONS.TRANSLATIONS_ACCESS,
      },
      {
        // EN-5178: replaces Retool's migrate_GlobalTranslations. Pulls from
        // the master store into THIS environment only, so unlike Retool it
        // exists in every environment — publishing everywhere means running
        // it once per environment.
        name: "Translations sync",
        path: "/admin/translations/sync",
        Icon: Book,
        requiredPermission: PERMISSIONS.TRANSLATIONS_SYNC_ACCESS,
      },
    ],
  },
  {
    name: "Access control",
    Icon: RuleIcon,
    children: [
      {
        name: "Role permissions",
        path: "/admin/access/roles",
        Icon: Forms,
        requiredPermission: PERMISSIONS.ROLE_PERMISSIONS_ACCESS,
      },
      {
        name: "User access",
        path: "/admin/access/users",
        Icon: RuleIcon,
        requiredPermission: PERMISSIONS.USER_ACCESS_ACCESS,
      },
    ],
  },
  {
    // The catalogues the app's medication search reads (EN-5209, EN-5240) —
    // not a patient's own medications (those are Treatments under Patients).
    name: "Medications",
    Icon: Pill,
    children: [
      {
        name: "SNOMED medications",
        path: "/admin/medications/snomed",
        Icon: Pill,
        requiredPermission: PERMISSIONS.MEDICATIONS_ACCESS,
      },
      {
        name: "SAM medications",
        path: "/admin/medications/sam",
        Icon: Pill,
        requiredPermission: PERMISSIONS.MEDICATIONS_ACCESS,
      },
      {
        // Per hospital: study medications and vitamins the care team confirmed.
        name: "Custom medications",
        path: "/admin/medications/custom",
        Icon: Pill,
        requiredPermission: PERMISSIONS.MEDICATIONS_ACCESS,
      },
    ],
  },
  {
    name: "Settings",
    Icon: Settings,
    children: [
      {
        name: "Audit logs",
        path: "/admin/settings/audit-logs",
        Icon: Database,
        requiredPermission: PERMISSIONS.AUDIT_LOGS_READ,
      },
      {
        name: "Email templates",
        path: "/admin/email-templates",
        Icon: Email,
        requiredPermission: PERMISSIONS.EMAIL_TEMPLATES_ACCESS,
      },
      {
        name: "AI prompt templates",
        path: "/admin/ai-prompt-templates",
        Icon: ResponseIcon,
        requiredPermission: PERMISSIONS.AI_PROMPT_TEMPLATES_ACCESS,
      },
      {
        name: "Scheduled jobs",
        path: "/admin/settings/scheduled-jobs",
        Icon: Calendar,
        requiredPermission: PERMISSIONS.QUARTZ_JOBS_ACCESS,
      },
    ],
  },
];

/**
 * Returns the admin menu the current user may see (EN-5129 per-page
 * permissions): entries with a `requiredPermission` the user doesn't hold
 * are stripped, entries whose `isAvailable` test fails in this environment
 * are stripped, and a group whose children were all stripped disappears
 * with them.
 */
export function getVisibleAdminMenuGroups(): IAdminRouteGroup[] {
  return adminMenuGroups
    .map((group) => {
      if (!group.children) return group;
      return {
        ...group,
        children: group.children.filter((item) => {
          if (
            item.requiredPermission &&
            !hasPermission(item.requiredPermission)
          )
            return false;
          return item.isAvailable ? item.isAvailable() : true;
        }),
      };
    })
    .filter((group) => {
      if (group.requiredPermission && !hasPermission(group.requiredPermission))
        return false;
      if (group.children) return group.children.length > 0;
      return true;
    });
}
